import {
    isPartnerCockpitSubscriptionEnded,
    type PartnerCockpitSubscription,
} from '@houzy/schemas/partner-cockpit/subscription';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { formatSwissAmount } from '../../../lib/formatSwissAmount';
import { PAYMENT_METHOD_PRESENTATION } from '../../../lib/paymentMethodPresentation';
import { subscriptionCadenceLabel } from '../../../lib/subscriptionCadence';
import { formatSwissDate } from '../../../lib/time';
import { stateColor } from '../../../theme';
import { subscriptionStatusLabel } from '../labels';

export type SubscriptionPlanCardProps = {
    subscription: PartnerCockpitSubscription;
};

export function SubscriptionPlanCard({ subscription }: SubscriptionPlanCardProps): ReactElement {
    const { t } = useTranslation();
    const { planName, pricePerMonth, commitmentTotal, nextBillingAt, nextProlongationAt } = subscription;
    const cancelEffectiveAt = isPartnerCockpitSubscriptionEnded(subscription) ? null : subscription.cancelEffectiveAt;
    const endsOn = cancelEffectiveAt ? formatSwissDate(new Date(cancelEffectiveAt)) : undefined;
    const paymentMethod = PAYMENT_METHOD_PRESENTATION[subscription.paymentMethod];

    const paymentLine = nextBillingAt
        ? t('SETTINGS.SUBSCRIPTION.PLAN.NEXT_PAYMENT', { date: formatSwissDate(new Date(nextBillingAt)) })
        : t('SETTINGS.SUBSCRIPTION.PLAN.NO_NEXT_BILLING');

    const termLine = endsOn
        ? t('SETTINGS.SUBSCRIPTION.PLAN.ENDS_ON', { date: endsOn })
        : nextProlongationAt
          ? t('SETTINGS.SUBSCRIPTION.PLAN.NEXT_PROLONGATION', { date: formatSwissDate(new Date(nextProlongationAt)) })
          : undefined;

    return (
        <Box
            sx={{
                px: 3,
                py: 3,
                borderRadius: 1,
                border: 1,
                borderColor: 'primary.main',
                bgcolor: (theme) => stateColor(theme, 'hover'),
            }}>
            <Stack spacing={2}>
                <Stack spacing={0.5}>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1">{planName}</Typography>
                        <Chip
                            size="small"
                            color={endsOn ? 'warning' : 'primary'}
                            label={
                                endsOn
                                    ? t('SETTINGS.SUBSCRIPTION.STATUS.CANCELS', { date: endsOn })
                                    : subscriptionStatusLabel(t, subscription.status)
                            }
                            sx={{ flexShrink: 0 }}
                        />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {paymentLine}
                    </Typography>
                    {termLine && (
                        <Typography variant="body2" color="text.secondary">
                            {termLine}
                        </Typography>
                    )}
                </Stack>

                <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                        <Typography variant="h4">{formatSwissAmount(pricePerMonth)}</Typography>
                        <Typography variant="body2">
                            {t('SETTINGS.SUBSCRIPTION.PLAN.PER_MONTH')} ·{' '}
                            {subscriptionCadenceLabel(t, subscription.billingCadence)}
                        </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: 'primary.main' }}>
                        {t('CHECKOUT.PLAN.COMMITMENT_TOTAL', { amount: formatSwissAmount(commitmentTotal) })}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('CHECKOUT.PAYMENT.TITLE')}
                    </Typography>
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={paymentMethod.icon}
                        label={t(`CHECKOUT.PAYMENT.${paymentMethod.copy}`)}
                    />
                </Stack>

                <Typography variant="caption" color="text.secondary">
                    {t('CHECKOUT.PLAN.PRICES_EXCL_VAT')}
                </Typography>
            </Stack>
        </Box>
    );
}
